import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import 'mantine-react-table/styles.css';

import {
    ActionsColumn,
    AgentStatusColumn,
    IdColumn,
    NameColumn,
    StatusColumn,
    TypeColumn,
} from './columns';
import {
    Box,
    Container,
    Flex,
    Group,
    MultiSelect,
    Portal,
    Select,
    Text,
} from '@mantine/core';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { MultiSelectCreatable, StatColumn } from '../../../common';
import {
    useDebouncedCallback,
    useDisclosure,
    useLocalStorage,
} from '@mantine/hooks';
import { useMemo, useState } from 'react';

import CampaignDetailsModal from './modals/CampaignDetailsModal';
import CampaignStartModal from './modals/CampaignStartModal';
import CampaignTableToolbar from './toolbars/CampaignTableToolbar';
import JsonOptionsModal from '../../../modals/options/JsonOptionsModal';
import ReplaceCampaignShortLinkModal from './modals/ReplaceCampaignShortLinkModal';
import SendTimelineModal from '../../../modals/timeline/SendTimelineModal';
import { getCampaigns } from '../../../../http/apiClient';
import { hasRole } from '../../../../utils/roles';
import { naiveRound } from '../../../../utils/text';
import { sumObjectKeys } from '../../../../utils/statUtils';
import { useAuthStore } from '../../../../store';
import { usePaginatedQuery } from '../../../../http/queries/usePaginatedQuery';

const CAMPAIGN_FILTER_OPTIONS = [
    {
        group: 'Status',
        items: [
            { label: 'Running', value: 'status_running' },
            { label: 'Paused', value: 'status_paused' },
            { label: 'Stopped', value: 'status_stopped' },
            {
                label: 'Complete',
                value: 'status_complete',
            },
            {
                label: 'Hide Paused',
                value: 'status_hide_paused',
            },
            {
                label: 'Hide Complete',
                value: 'status_hide_complete',
            },
        ],
    },
    {
        group: 'Admin',
        role: 'SUPER_ADMIN',
        items: [
            {
                label: 'Hide Admin',
                value: 'admin_hide',
            },
            {
                label: 'Hide Others',
                value: 'admin_hide_others',
            },
        ],
    },
];

const CampaignsView = ({ ...props }) => {
    const userProfile = useAuthStore((state) => state.userProfile);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 50,
    });

    const filterDebounce = useDebouncedCallback((query) => {
        setGlobalFilter(query);
    }, 600);

    const [
        detailsModalOpen,
        { open: openDetailsModal, close: closeDetailsModal },
    ] = useDisclosure();

    const [
        replaceShortLinkModalOpen,
        { open: openReplaceShortLinkModal, close: closeReplaceShortLinkModal },
    ] = useDisclosure();

    const [
        startCampaignModalOpen,
        { open: openStartCampaignModal, close: closeStartCampaignModal },
    ] = useDisclosure();

    const [jsonModalOpen, { open: openJsonModal, close: closeJsonModal }] =
        useDisclosure();

    const [
        timelineModalOpen,
        { open: openTimelineModal, close: closeTimelineModal },
    ] = useDisclosure();

    const [jsonModalProps, setJsonModalProps] = useState({});
    const [modalTab, setModalTab] = useState('events');
    const [modalCampaign, setModalCampaign] = useState(null);

    const [columnVisibility, setColumnVisibility] = useLocalStorage({
        key: 'campaign-table-visibility', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const [columnSizing, setColumnSizing] = useLocalStorage({
        key: 'campaign-table-sizing', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const [activeFilters, setActiveFilters] = useLocalStorage({
        key: 'campaign-table-filters', // a unique key for local storage
        defaultValue: [], // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const {
        isLoading,
        isError,
        error,
        data: paginatedData,
    } = usePaginatedQuery({
        queryFn: (queryParams) =>
            getCampaigns({
                pagination: queryParams,
            }),
        queryKey: ['campaigns'],
        refetchInterval: 15_0000,
        globalFilter,
        pagination,
        sorting,
        filters: activeFilters,
    });

    const { data: campaigns, pagination: campaignPagination } =
        paginatedData || {};
    const columns = useMemo(
        () => [
            IdColumn(),
            NameColumn(),
            //TypeColumn(),
            StatusColumn(),
            StatColumn('Sent', (x) => x.stats?.send, false),
            StatColumn(
                'Failed',
                (x) => sumObjectKeys(x.stats, ['sendFail', 'sendTimeout']),
                false,
                true
            ),
            StatColumn('Clicks', (x) =>
                sumObjectKeys(x.stats, ['uniqueClick'])
            ),
            StatColumn('Previews', (x) =>
                sumObjectKeys(x.stats, ['uniquePreview'])
            ),
            StatColumn(
                'CTR',
                (x) => {
                    const uniqueClicks = sumObjectKeys(x.stats, [
                        'uniqueClick',
                    ]);
                    return naiveRound((uniqueClicks / x.stats?.send) * 100, 2);
                },
                true
            ),
            StatColumn('Conversions', (x) => x.stats?.conversion),
            StatColumn(
                'CR',
                (x) => {
                    const uniqueClicks = sumObjectKeys(x.stats, [
                        'uniqueClick',
                    ]);
                    return naiveRound(
                        (x.stats?.conversion / uniqueClicks) * 100,
                        2
                    );
                },
                true
            ),
            /*StatColumn('Conversations', (x) => x.stats?.conversation),
            StatColumn('Received', (x) => x.stats?.received),*/
            ActionsColumn({
                setTab: setModalTab,
                setCampaign: setModalCampaign,
                openDetailsModal,
                openStartModal: openStartCampaignModal,
                openReplaceShortLinkModal,
                openTimelineModal,
                openJsonModal,
                setJsonModalProps,
            }),
        ],
        []
    );

    const tableData = useMemo(
        () => (isLoading ? [] : (campaigns ?? [])),
        [campaigns, isLoading]
    );

    const filterOptions = useMemo(
        () =>
            CAMPAIGN_FILTER_OPTIONS.filter(
                (x) => x.role == null || hasRole(userProfile, x.role)
            ),
        [userProfile?.roles]
    );

    const handleVisibilityChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnVisibility((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handlSizeChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnSizing((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handleQueryFilterChange = (queryStr) => {
        filterDebounce(queryStr);
    };

    const handleFilterChange = (values) => {
        setActiveFilters(values);
    };

    const table = useMantineReactTable({
        columns: columns,
        data: tableData,
        memoMode: 'cells',
        getRowId: (row) => row.id,
        enableRowActions: false,
        enableColumnResizing: true,
        enableColumnActions: false,
        enableSorting: false,
        enableTopToolbar: true,
        enableGlobalFilter: true,
        enableFilters: false,
        enableStickyHeader: true,
        enableDensityToggle: false,
        columnResizeMode: 'onEnd',
        positionActionsColumn: 'last',
        layoutMode: 'semantic',
        mantineSearchTextInputProps: {
            placeholder: 'Search Campaigns',
            miw: 200,
        },
        mantineTableContainerProps: {
            style: {
                minHeight: '80vh',
            },
        },
        positionGlobalFilter: 'left',
        renderTopToolbarCustomActions: ({ props: table }) => (
            <CampaignTableToolbar
                table={table}
                activeFilters={activeFilters}
                filterOptions={filterOptions}
                onFilterChange={handleFilterChange}
            />
        ),
        manualPagination: true,
        manualSorting: true,
        onColumnVisibilityChange: handleVisibilityChange,
        onColumnSizingChange: handlSizeChange,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onGlobalFilterChange: handleQueryFilterChange,
        rowCount: campaignPagination?.totalItems || 0,
        pageCount: campaignPagination?.totalPages || 1,

        initialState: {
            columnVisibility,
            pagination,
            isLoading,
            showGlobalFilter: true,
        },
        state: {
            isLoading,
            pagination,
            columnVisibility,
            columnSizing,
            showAlertBanner: isError,
            showProgressBars: isLoading,
            sorting,
            density: 'xs',
        },
    });

    return (
        <Container fluid>
            <MantineReactTable table={table} />
            <CampaignDetailsModal
                campaign={modalCampaign}
                openTab={modalTab}
                opened={detailsModalOpen}
                close={closeDetailsModal}
            />
            <CampaignStartModal
                campaign={modalCampaign}
                opened={startCampaignModalOpen}
                close={closeStartCampaignModal}
            />
            <ReplaceCampaignShortLinkModal
                campaign={modalCampaign}
                opened={replaceShortLinkModalOpen}
                close={closeReplaceShortLinkModal}
            />
            <JsonOptionsModal
                {...jsonModalProps}
                opened={jsonModalOpen}
                close={closeJsonModal}
            />
            <SendTimelineModal
                campaign={modalCampaign}
                opened={timelineModalOpen}
                close={closeTimelineModal}
            />
        </Container>
    );
};
export default CampaignsView;
